//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { IUrlRouterProvider } from 'angular-ui-router';

import {
  IPartialState,
  NavGroup,
  StateFactory,
  componentNameToTemplate,
  hasAllFeatures,
  hasFeature,
  lazyLoadModule,
} from 'app/initialization/routing/stateHelpers';

function CommunityStateFactory(state: IPartialState) {
  const accessFn = state.data?.hasAccess ?? (() => true);
  const data = {
    title: 'Community',
    navGroup: NavGroup.COMMUNITY,
    ...state.data,
    hasAccess: () =>
      hasAllFeatures(FeatureNames.CreatorCommunity) && accessFn(),
  };

  return StateFactory({
    ...state,
    data,
  });
}

export function routingRules(urlRouterProvider: IUrlRouterProvider) {
  const isCreatorCommunityEnabled = hasFeature(FeatureNames.CreatorCommunity);

  if (!isCreatorCommunityEnabled) {
    urlRouterProvider.when('/creator-community/*', '/');
  }
}

export const states = {
  community: CommunityStateFactory({
    url: '/creator-community',
    template: componentNameToTemplate('creatorCommunityPage'),
    resolve: {
      ...lazyLoadModule(
        'curalate.creator-community',
        () => import('@creator-community/CreatorCommunityApp'),
      ),
    },
    redirectTo: 'community.manage',
  }),

  'community.manage': CommunityStateFactory({
    url: '/manage',
    data: {
      title: 'Manage Community',
    },
  }),
  'community.manage-edit': CommunityStateFactory({
    url: '/manage/edit',
    data: {
      title: 'Edit Community',
    },
    redirectTo: 'community.manage-edit-landing-image',
  }),

  'community.manage-edit-landing-image': CommunityStateFactory({
    url: '/manage/edit/landing-image',
    data: {
      title: 'Edit Community Landing Image',
    },
  }),

  'community.manage-edit-landing-hero': CommunityStateFactory({
    url: '/manage/edit/landing-hero',
    data: {
      title: 'Edit Community Landing Hero',
    },
  }),

  'community.manage-edit-landing-colors': CommunityStateFactory({
    url: '/manage/edit/landing-colors',
    data: {
      title: 'Edit Community Landing Colors',
    },
  }),

  'community.manage-edit-application-image': CommunityStateFactory({
    url: '/manage/edit/application-image',
    data: {
      title: 'Edit Community Application Image',
    },
  }),

  'community.manage-edit-application-basic-info': CommunityStateFactory({
    url: '/manage/edit/application-basic-info',
    data: {
      title: 'Edit Community Application Basic Info',
    },
  }),

  'community.manage-edit-application-shipping-info': CommunityStateFactory({
    url: '/manage/edit/application-shipping-info',
    data: {
      title: 'Edit Community Application Shipping Info',
    },
  }),

  'community.manage-edit-application-questions': CommunityStateFactory({
    url: '/manage/edit/application-questions',
    data: {
      title: 'Edit Community Application Questions',
    },
  }),

  'community.manage-edit-application-social-media-info': CommunityStateFactory({
    url: '/manage/edit/application-social-media-info',
    data: {
      title: 'Edit Community Application Social Media Info',
    },
  }),

  'community.members': CommunityStateFactory({
    url: '/members',
    data: {
      title: 'Community Members',
    },
  }),

  'community.applicants': CommunityStateFactory({
    url: '/applicants',
    data: {
      title: 'Community Applicants',
    },
    redirectTo: 'community.applicants-received',
  }),

  'community.applicants-received': CommunityStateFactory({
    url: '/applicants/received',
    data: {
      title: 'Community Applicants Received',
    },
  }),

  'community.applicants-rejected': CommunityStateFactory({
    url: '/applicants/rejected',
    data: {
      title: 'Community Applicants Rejected',
    },
  }),
};
